class ExploreCollection extends HTMLElement {
  connectedCallback() {
    this._track = this.querySelector('.ec-track');
    this._indicator = this.querySelector('.ec-progress-indicator');
    this._progressTrack = this._indicator?.parentElement;
    this._slides = this.querySelectorAll('.ec-slide');

    if (!this._track || !this._indicator || !this._progressTrack) return;

    this._currentIndex = 0;
    this._autoPlayTimer = null;

    // Bind document-level handlers
    this._onScroll = this._updateProgress.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onTouchMove = this._handleTouchMove.bind(this);
    this._onTouchEnd = this._handleTouchEnd.bind(this);
    this._onDocumentClick = this._handleDocumentClick.bind(this);

    // Track scroll
    this._track.addEventListener('scroll', this._onScroll, { passive: true });

    // Document-level drag handlers
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
    document.addEventListener('touchmove', this._onTouchMove, { passive: true });
    document.addEventListener('touchend', this._onTouchEnd);

    // Hotspot document click handler
    document.addEventListener('click', this._onDocumentClick);

    // Prevent document click from firing when clicking inside this section
    this.addEventListener('click', (e) => e.stopPropagation());

    // Auto-play pause on hover
    this.addEventListener('mouseenter', () => this._stopAutoPlay());
    this.addEventListener('mouseleave', () => this._startAutoPlay());

    this._initDrag();
    this._initHotspots();
    this._initQuickView();
    this._updateProgress();
    this._startAutoPlay();
  }

  disconnectedCallback() {
    this._track?.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend', this._onTouchEnd);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._onResize);
    this._cancelMomentum();
    this._stopAutoPlay();
  }

  // ── Progress ─────────────────────────────────────────────────

  _updateProgress() {
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;
    if (maxScroll <= 0) {
      this._indicator.style.width = '100%';
      this._indicator.style.left = '0%';
      return;
    }
    const isMobile = window.innerWidth <= 749;
    const maxIndicatorPx = isMobile ? 42 : 379;
    const trackW = this._progressTrack.clientWidth;
    const visibleRatio = this._track.clientWidth / this._track.scrollWidth;
    const indicatorPx = Math.min(Math.max(visibleRatio * trackW, trackW * 0.08), maxIndicatorPx);
    const indicatorW = (indicatorPx / trackW) * 100;
    const progress = this._track.scrollLeft / maxScroll;
    this._indicator.style.width = indicatorW + '%';
    this._indicator.style.left = progress * (100 - indicatorW) + '%';
  }

  // ── Drag ─────────────────────────────────────────────────────

  _initDrag() {
    this._dragging = false;
    this._indicatorDragging = false;
    this._velocity = 0;
    this._momentumRaf = null;

    // ── Mouse drag on track ──────────────────────────────────
    this._track.addEventListener('mousedown', (e) => {
      this._cancelMomentum();
      this._dragging = true;
      this._dragStartX = e.pageX;
      this._dragScrollStart = this._track.scrollLeft;
      this._dragLastX = e.pageX;
      this._dragLastTime = performance.now();
      this._velocity = 0;
      this._track.classList.add('is-dragging');
      this._stopAutoPlay();
      e.preventDefault();
    });

    // ── Touch: let the browser handle native scroll + momentum ──
    this._track.addEventListener('touchstart', () => {
      this._cancelMomentum();
      this._stopAutoPlay();
    }, { passive: true });

    this._track.addEventListener('touchend', () => {
      // Wait for native snap to settle, then sync index and resume
      setTimeout(() => {
        this._updateCurrentIndex();
        this._restartAutoPlay();
      }, 400);
    }, { passive: true });

    // ── Indicator drag ────────────────────────────────────────
    this._indicator.addEventListener('mousedown', (e) => {
      this._cancelMomentum();
      this._startIndicatorDrag(e.clientX);
      this._stopAutoPlay();
      e.preventDefault();
      e.stopPropagation();
    });

    this._indicator.addEventListener('touchstart', (e) => {
      this._startIndicatorDrag(e.touches[0].clientX);
      this._stopAutoPlay();
      e.stopPropagation();
    }, { passive: true });
  }

  _startIndicatorDrag(clientX) {
    this._indicatorDragging = true;
    this._indicatorStartX = clientX;
    this._indicatorScrollStart = this._track.scrollLeft;
    this._indicator.style.transition = 'none';
  }

  _moveIndicator(clientX) {
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;
    if (maxScroll <= 0) return;
    const availablePx = this._progressTrack.clientWidth - this._indicator.offsetWidth;
    if (availablePx <= 0) return;
    const dx = clientX - this._indicatorStartX;
    this._track.scrollLeft = Math.max(0, Math.min(
      this._indicatorScrollStart + (dx / availablePx) * maxScroll,
      maxScroll
    ));
  }

  _handleMouseMove(e) {
    if (this._dragging) {
      const now = performance.now();
      const dt = now - this._dragLastTime;
      if (dt > 0) {
        // Weighted blend of instant velocity for smooth tracking
        const instant = (this._dragLastX - e.pageX) / dt;
        this._velocity = this._velocity * 0.6 + instant * 0.4;
      }
      this._dragLastX = e.pageX;
      this._dragLastTime = now;
      this._track.scrollLeft = this._dragScrollStart - (e.pageX - this._dragStartX);
    }
    if (this._indicatorDragging) {
      this._moveIndicator(e.clientX);
    }
  }

  _handleMouseUp() {
    if (this._dragging) {
      this._dragging = false;
      // Keep is-dragging active through momentum so CSS snap doesn't fight RAF
      this._startMomentum();
    }
    if (this._indicatorDragging) {
      this._indicatorDragging = false;
      this._indicator.style.transition = '';
      this._restartAutoPlay();
    }
  }

  _handleTouchMove(e) {
    if (this._indicatorDragging) {
      this._moveIndicator(e.touches[0].clientX);
    }
  }

  _handleTouchEnd() {
    if (this._indicatorDragging) {
      this._indicatorDragging = false;
      this._indicator.style.transition = '';
      this._restartAutoPlay();
    }
  }

  // ── Momentum ──────────────────────────────────────────────────

  _startMomentum() {
    const minVelocity = 0.04; // px/ms threshold to stop
    if (Math.abs(this._velocity) < minVelocity) {
      this._snapToNearest();
      this._restartAutoPlay();
      return;
    }

    const friction = 0.92; // per-frame multiplier at 60fps
    let lastTime = performance.now();

    const step = (now) => {
      const dt = now - lastTime;
      lastTime = now;
      // Frame-rate independent friction
      this._velocity *= Math.pow(friction, dt / 16);
      this._track.scrollLeft += this._velocity * dt;

      if (Math.abs(this._velocity) < minVelocity) {
        this._velocity = 0;
        this._momentumRaf = null;
        this._snapToNearest();
        this._restartAutoPlay();
        return;
      }
      this._momentumRaf = requestAnimationFrame(step);
    };

    this._momentumRaf = requestAnimationFrame(step);
  }

  _cancelMomentum() {
    if (this._momentumRaf) {
      cancelAnimationFrame(this._momentumRaf);
      this._momentumRaf = null;
      this._track.style.scrollBehavior = '';
      this._track.classList.remove('is-dragging');
    }
    this._velocity = 0;
  }

  _snapToNearest() {
    if (!this._slides.length) return;
    const slideW = this._slides[0].offsetWidth;
    const gap = 12;
    const index = Math.round(this._track.scrollLeft / (slideW + gap));
    this._currentIndex = Math.max(0, Math.min(index, this._slides.length - 1));
    const target = this._slides[this._currentIndex].offsetLeft;
    // Smooth snap — keep is-dragging so CSS snap-type doesn't interfere
    this._track.style.scrollBehavior = 'smooth';
    this._track.scrollLeft = target;
    setTimeout(() => {
      this._track.style.scrollBehavior = '';
      this._track.classList.remove('is-dragging');
    }, 450);
  }

  // ── Navigation ───────────────────────────────────────────────

  _goTo(index) {
    if (!this._slides.length) return;
    this._cancelMomentum();
    if (index < 0) {
      index = this._slides.length - 1;
    } else if (index >= this._slides.length) {
      index = 0;
    }
    const slide = this._slides[index];
    this._track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this._currentIndex = index;
    this._restartAutoPlay();
  }

  _updateCurrentIndex() {
    if (!this._slides.length) return;
    const gap = 12;
    const slideWidth = this._slides[0].offsetWidth;
    const divisor = slideWidth + gap;
    if (divisor > 0) {
      this._currentIndex = Math.round(this._track.scrollLeft / divisor);
      this._currentIndex = Math.max(0, Math.min(this._currentIndex, this._slides.length - 1));
    }
  }

  // ── Auto-play ────────────────────────────────────────────────

  _startAutoPlay() {
    this._stopAutoPlay();
    this._autoPlayTimer = setInterval(() => {
      this._goTo(this._currentIndex + 1);
    }, 8000);
  }

  _stopAutoPlay() {
    if (this._autoPlayTimer) {
      clearInterval(this._autoPlayTimer);
      this._autoPlayTimer = null;
    }
  }

  _restartAutoPlay() {
    this._stopAutoPlay();
    this._startAutoPlay();
  }

  // ── Hotspots ─────────────────────────────────────────────────

  _initHotspots() {
    const wrappers = this.querySelectorAll('.ec-hotspot-wrapper');

    wrappers.forEach((wrapper) => {
      this._setHotspotDirection(wrapper);
    });

    wrappers.forEach((wrapper) => {
      const btn = wrapper.querySelector('.ec-hotspot');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const handle = btn.getAttribute('data-product-handle');
        const url = btn.getAttribute('data-product-url');
        if (handle) this._openQuickView(handle, url);
      });
    });
  }

  _handleDocumentClick() {
    // No-op: hotspot card stays hidden; quickview has its own close handlers
  }

  _setHotspotDirection(wrapper) {
    const slide = wrapper.closest('.ec-slide');
    if (!slide) return;

    const leftPct = parseFloat(wrapper.style.left) || 50;
    const topPct = parseFloat(wrapper.style.top) || 50;
    const slideW = slide.offsetWidth || 603;
    const slideH = slide.offsetHeight || 820;

    // Card is 263px wide, hotspot button is 40px, gap is 16px
    // Opens left if card would overflow: hotspot_x + 40 + 16 + 263 > slideW
    const hotspotX = (leftPct / 100) * slideW;
    if (hotspotX + 40 + 16 + 263 > slideW) {
      wrapper.classList.add('opens-left');
    }

    // Card approx 160px tall; opens up if card would overflow bottom
    const hotspotY = (topPct / 100) * slideH;
    if (hotspotY + 160 > slideH) {
      wrapper.classList.add('opens-up');
    }
  }

  _quickAdd(btn, variantId) {
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(() => {
        btn.textContent = 'Added \u2713';
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: { source: 'product-form-component', itemCount: cart.item_count }
          }
        }));
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
      })
      .catch(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      });
  }

  // ── Quick View ────────────────────────────────────────────────

  _initQuickView() {
    this._quickview = this.closest('.ec-section')?.querySelector('.ec-quickview');
    if (!this._quickview) return;

    this._qvDrawer = this._quickview.querySelector('.ec-quickview__drawer');
    this._qvContent = this._quickview.querySelector('.ec-quickview__content');
    this._qvLoading = this._quickview.querySelector('.ec-quickview__loading');
    this._qvOverlay = this._quickview.querySelector('.ec-quickview__overlay');

    // Close via overlay click
    this._qvOverlay?.addEventListener('click', () => this._closeQuickView());

    // Escape key
    this._onKeyDown = (e) => {
      if (e.key === 'Escape') {
        this._closeQuickView();
        return;
      }
      if (e.key === 'Tab') this._trapFocus(e);
    };
    document.addEventListener('keydown', this._onKeyDown);

    // Disable drawer transition during resize
    let resizeTimer;
    this._onResize = () => {
      if (this._qvDrawer) {
        this._qvDrawer.style.transition = 'none';
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this._qvDrawer.style.transition = '';
        }, 300);
      }
      this.querySelectorAll('.ec-hotspot-wrapper.is-active').forEach((w) => {
        w.classList.remove('is-active');
      });
    };
    window.addEventListener('resize', this._onResize);
  }

  _openQuickView(handle, productUrl) {
    if (!this._quickview) return;
    // The drawer is a modal dialog, so remember the control that opened it and send focus
    // back there on close (WCAG 2.4.3).
    this._previouslyFocused = document.activeElement;
    this._quickview.setAttribute('aria-hidden', 'false');
    this._quickview.inert = false;
    this._quickview.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    this._qvContent.innerHTML = '';
    this._qvLoading?.classList.add('is-active');

    fetch(`/products/${handle}?sections=ec-quickview-product`)
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.json();
      })
      .then((data) => {
        const html = data['ec-quickview-product'];
        if (!html) throw new Error('Section returned null');
        this._qvContent.innerHTML = html;
        this._qvLoading?.classList.remove('is-active');
        this._initQuickViewInteractions(productUrl);
        this._focusDrawer();
        this._reloadStampedBadges();
      })
      .catch(() => {
        this._qvLoading?.classList.remove('is-active');
        this._qvContent.innerHTML =
          '<div class="ec-qv__panel" style="justify-content:center;align-items:center">' +
          '<p style="padding:16px;font-family:Figtree,sans-serif;font-size:14px;color:#555">' +
          'Could not load product. <a href="' + productUrl + '" style="color:#1f1f1f;text-decoration:underline">View full details</a></p></div>';
        this._focusDrawer();
      });
  }

  _initQuickViewInteractions(productUrl) {
    const content = this._qvContent;

    // Progress bar for mobile image carousel
    const qvImages = content.querySelector('.ec-qv__images');
    const qvIndicator = content.querySelector('.ec-qv__progress-indicator');
    if (qvImages && qvIndicator) {
      const updateQvProgress = () => {
        const maxScroll = qvImages.scrollWidth - qvImages.clientWidth;
        if (maxScroll <= 0) {
          qvIndicator.style.width = '100%';
          qvIndicator.style.left = '0%';
          return;
        }
        const track = qvIndicator.parentElement;
        const trackW = track.clientWidth;
        const visibleRatio = qvImages.clientWidth / qvImages.scrollWidth;
        const indicatorPx = Math.min(Math.max(visibleRatio * trackW, trackW * 0.08), 42);
        const indicatorW = (indicatorPx / trackW) * 100;
        const progress = qvImages.scrollLeft / maxScroll;
        qvIndicator.style.width = indicatorW + '%';
        qvIndicator.style.left = progress * (100 - indicatorW) + '%';
      };
      qvImages.addEventListener('scroll', updateQvProgress, { passive: true });
      updateQvProgress();
    }

    // JS-driven horizontal swipe (overflow: hidden in CSS, no native scroll).
    // Same pattern as sf-product-media-scroll.js mobile: distance OR velocity
    // threshold — a short flick changes slide, matching iOS/Android expectations.
    if (qvImages) {
      let tStartX = 0, tStartScroll = 0, tStartTime = 0, tActive = false;

      qvImages.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        tStartX = e.touches[0].clientX;
        tStartScroll = qvImages.scrollLeft;
        tStartTime = performance.now();
        tActive = true;
      }, { passive: true });

      qvImages.addEventListener('touchmove', (e) => {
        if (!tActive) return;
        const dx = tStartX - e.touches[0].clientX;
        qvImages.scrollLeft = tStartScroll + dx;
      }, { passive: true });

      qvImages.addEventListener('touchend', (e) => {
        if (!tActive) return;
        tActive = false;
        const endX = e.changedTouches[0]?.clientX ?? tStartX;
        const dx = tStartX - endX;
        const elapsed = performance.now() - tStartTime;
        const velocity = elapsed > 0 ? dx / elapsed : 0;
        const items = qvImages.querySelectorAll('.ec-qv__image-item');
        const w = items[0]?.offsetWidth;
        if (!items.length || !w) return;
        const currentIdx = Math.round(tStartScroll / w);
        const distThreshold = w * 0.15;
        const velThreshold = 0.25;
        let targetIdx = currentIdx;
        if (Math.abs(dx) > distThreshold || Math.abs(velocity) > velThreshold) {
          targetIdx = currentIdx + (dx > 0 ? 1 : -1);
        }
        targetIdx = Math.max(0, Math.min(items.length - 1, targetIdx));
        qvImages.scrollTo({ left: targetIdx * w, behavior: 'smooth' });
      }, { passive: true });
    }

    // Parse product JSON
    const jsonEl = content.querySelector('script[data-ec-qv-product]');
    let productData = null;
    if (jsonEl) {
      try { productData = JSON.parse(jsonEl.textContent); } catch (e) { /* noop */ }
    }

    // Track selected options
    const selectedOptions = [];
    if (productData) {
      const selectedVariant = productData.variants.find((v) => v.id === productData.selected_variant_id);
      if (selectedVariant) {
        selectedVariant.options.forEach((opt) => selectedOptions.push(opt));
      }
    }

    // Close button
    const closeBtn = content.querySelector('.ec-qv__close');
    closeBtn?.addEventListener('click', () => this._closeQuickView());

    // Option selection (swatches, sizes, buttons)
    content.querySelectorAll('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.value;

        // Update selected state in UI
        const parent = btn.closest('.ec-qv__option');
        parent.querySelectorAll('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn').forEach((b) => {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        // Update option label
        const selectedLabel = parent.querySelector('[data-qv-option-selected]');
        if (selectedLabel) selectedLabel.textContent = value.toUpperCase();

        // Find matching variant
        selectedOptions[idx] = value;
        if (productData) {
          const variant = productData.variants.find((v) =>
            v.options.every((opt, i) => opt === selectedOptions[i])
          );
          if (variant) this._updateQuickViewVariant(variant);
        }
      });
    });

    // Add to bag
    const addBtn = content.querySelector('.ec-qv__add-to-bag');
    addBtn?.addEventListener('click', () => {
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;
      this._qvAddToCart(addBtn, variantId);
    });
  }

  _updateQuickViewVariant(variant) {
    const content = this._qvContent;

    // Update price
    const priceEl = content.querySelector('[data-qv-price]');
    if (priceEl) {
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml += '<span class="ec-qv__compare-price">' + variant.compare_at_price_formatted + '</span>';
      }
      priceHtml += '<span class="ec-qv__current-price">' + variant.price_formatted + '</span>';
      priceEl.innerHTML = priceHtml;
    }

    // Update add to bag button
    const addBtn = content.querySelector('.ec-qv__add-to-bag');
    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.innerHTML = 'ADD TO BAG <span class="ec-qv__bag-plus">[+]</span>';
      } else {
        addBtn.disabled = true;
        addBtn.innerHTML = 'SOLD OUT <span class="ec-qv__bag-plus">[+]</span>';
      }
    }

    // Scroll to variant featured image if it exists
    if (variant.featured_image) {
      const images = content.querySelectorAll('.ec-qv__img');
      images.forEach((img) => {
        if (img.src && img.src.includes(variant.featured_image.split('?')[0].split('/').pop())) {
          img.closest('.ec-qv__image-item')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }

  _qvAddToCart(btn, variantId) {
    const originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'ADDING... <span class="ec-qv__bag-plus">[+]</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(() => {
        btn.innerHTML = 'ADDED ✓ <span class="ec-qv__bag-plus">[+]</span>';

        // Close quickview first
        this._closeQuickView();

        // Fetch updated cart to get item_count for header badge
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        // Open AMP Slide Cart Drawer if available
        if (typeof window.SLIDECART_UPDATE === 'function') {
          window.SLIDECART_UPDATE();
          if (typeof window.SLIDECART_OPEN === 'function') {
            window.SLIDECART_OPEN();
          }
        }

        // Dispatch cart:update so sf-header badge + cart-drawer update
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, data: { itemCount: cart.item_count } }
        }));

        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }, 2000);
      })
      .catch(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
  }

  _closeQuickView() {
    if (!this._quickview) return;
    this._quickview.classList.remove('is-open');
    this._quickview.setAttribute('aria-hidden', 'true');
    // Off-screen content must leave the tab order too (WCAG 4.1.2 aria-hidden-focus)
    this._quickview.inert = true;
    document.body.style.overflow = '';
    this._previouslyFocused?.focus?.({ preventScroll: true });
    this._previouslyFocused = null;
  }

  // The drawer's content arrives from the Section Rendering API, so its focusable elements
  // only exist after the fetch resolves -- these three run from there, never at init.
  _focusableInDrawer() {
    if (!this._qvDrawer) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this._qvDrawer.querySelectorAll(selector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0
    );
  }

  _focusDrawer() {
    const target = this._qvDrawer?.querySelector('.ec-qv__close') || this._focusableInDrawer()[0] || this._qvDrawer;
    if (!target) return;
    if (target === this._qvDrawer) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  // Keeps Tab inside the open dialog. The page behind it is not inert while open, so without
  // this a keyboard user tabs straight out of the drawer and into the page underneath.
  _trapFocus(e) {
    if (!this._quickview?.classList.contains('is-open')) return;
    const focusable = this._focusableInDrawer();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!this._qvDrawer.contains(active)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Render Stamped.io review badges inside the quickview.
  //
  // Stamped's script scans the DOM only once on load, so badges injected
 
  _reloadStampedBadges() {
    const badges = this._qvContent?.querySelectorAll('.stamped-product-reviews-badge');
    if (!badges || !badges.length) return;

    badges.forEach((badge) => {
      const productId = badge.getAttribute('data-id');
      if (!productId) return;

      // Find any OTHER badge for the same product already rendered on the page
      const sourceSelector = `.stamped-product-reviews-badge[data-id="${CSS.escape(productId)}"]`;
      const candidates = document.querySelectorAll(sourceSelector);
      let source = null;
      for (const el of candidates) {
        if (el === badge) continue;
        if (this._qvContent?.contains(el)) continue;
        // Pick one that Stamped has already filled
        if (el.innerHTML.trim().length > 0 && el.querySelector('.stamped-badge, .stamped-fa-star, .stamped-badge-starrating')) {
          source = el;
          break;
        }
      }

      if (source) {
        badge.innerHTML = source.innerHTML;
        return;
      }

      // Fallback: try the Stamped API surface (covers all known versions)
      this._stampedApiReload();
    });


    this._stampedApiReload();
  }

  _stampedApiReload() {
    const tryReload = () => {
      const fn = window.StampedFn;
      if (fn && typeof fn.reloadBadges === 'function') { fn.reloadBadges(); return true; }
      if (fn && typeof fn.loadBadges === 'function') { fn.loadBadges(); return true; }
      if (fn && typeof fn.init === 'function') { fn.init(); return true; }

      const s = window.Stamped || window.stamped;
      if (s && typeof s.reloadBadges === 'function') { s.reloadBadges(); return true; }
      if (s && typeof s.loadBadges === 'function') { s.loadBadges(); return true; }
      if (s && typeof s.init === 'function') { s.init(); return true; }

      if (window.stampedJS && typeof window.stampedJS.reload === 'function') {
        window.stampedJS.reload();
        return true;
      }
      return false;
    };

    if (tryReload()) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (tryReload() || attempts >= 20) clearInterval(interval);
    }, 250);
  }
}

if (!customElements.get('explore-collection')) {
  customElements.define('explore-collection', ExploreCollection);
}
